import { Indexed } from "@services/common/types";

export type IndexedAccount = Account & Indexed;

export type Issues = string[] & { id: string; name: IssueName };

export type IssueName = "type" | "source" | "resolution";
export interface TicketsAware {
  qualityOfExperience?: number;
  numOpenTickets?: number;
  ticketPriority?: Priority;
}

export interface Account extends TicketsAware {
  details?: AccountDetails;
  accountEntitlementOptions?: AccountEntitlementOptions;
  summary?: AccountSummary;
  commProxyEnabled?: boolean;
  id: string;
  active?: boolean;
  logo?: string;
  name?: string;
  readOnly?: boolean;
  notes?: string;
  type?: string;
  advancedBillingEnabled?: boolean;
  exchangeEnabledCloud?: boolean;
  logoId?: number;
  parentAccount?: Account;
  recordingEnabled?: boolean;
  exchangeEnabled?: boolean;
  services?: Array<Service>;
  totalFilesSize?: number;
  filesSizeLimit?: number;
  numFiles?: number;
  contractStartDate?: number;
  contractEndDate?: number;
  referenceNumber?: string;
  consumption?: string;
  cloudConnector?: string;
  entityType?: string;
  tenantAccount?: Account;
  authenticationMethod?: string;
  tags?: string[];
}

export interface AccountEntitlementOptions<T = EntitlementOption> {
  dateExpired?: number;
  options?: {
    deviceMonitoring: T;
    roomMonitoring: T;
    externalTicketing: T;
    externalScheduling: T;
    accountCustomization: T;
    deviceControls: T;
    deviceArchive: T;
    supportDmca: T;
    supportController: T;
    supportAggregator: T;
    supportMultipleMonitoringSources: T;
    workflows: T;
    aiTroubleshooting: T;
    sso: T;
    buildYourOwnReports: T;
    premier: T;
    reports: T;
    analytics: T;
    attachments: T;
    enhancedPasswordsSecurity: T;
    vmr: T;
    liteMonitoring: T;
    auditTrails: T;
    extendedPropertyMapping: T;
    assetTagging?: T;
  };
}
export type AccountEntitlementOptionKeys =
  keyof Required<AccountEntitlementOptions>["options"];

export interface EntitlementOption {
  option?: Option;
  usageLimit?: number;
  serviceProvider?: ServiceProvider;
  quantity?: number;
  enabled?: boolean;
}

export interface AccountSummary {
  totalChildAccounts?: number;
  accountId?: string;
  totalRooms?: number;
  totalUsers?: number;
  totalDevices?: number;
  totalCountries?: number;
  totalLocations?: number;
  totalAuthors?: number;
}

export interface AccountBranding {
  accountDetails?: AccountDetails;
  logo?: string;
  description?: BrandingDescription;
  id?: number;
  vmrBrandingTheme?: VmrBrandingTheme;
  logoId?: number;
}

export interface VmrBrandingTheme {
  accountBranding?: AccountBranding;
  name?: string;
  id?: number;
  resourceUri?: string;
}

export interface AccountUserRegistration {
  defaultRoleId?: number;
  selfRegistrationDomain?: string;
  defaultService?: Service;
  selfRegistrationDomains?: Array<AccountUserRegistrationDomain>;
  selfRegistrationEnabled: boolean;
  accountId?: string;
  defaultVmrService?: Service;
}

export interface AccountUserRegistrationDomain {
  id?: AccountUserRegistrationDomainId;
  domain?: string;
  accountUserRegistration?: AccountUserRegistration;
}

export interface AccountUserRegistrationDomainId {
  accountId?: string;
  domain?: string;
}

export interface CustomizedProperty {
  name?: string;
  position?: number;
}

export interface BillingCode {
  description?: string;
  /**
   * @value Between -1 and +Infinity; -1 meaning that it's reported to the parent.
   * Only valid for HOSTED_MCUs
   */
  codeLimit?: number;
  billingCode?: string;
  type?: Type;
  frequent?: boolean;
  active?: boolean;
}

export interface Recorder {
  enabled: boolean;
  storageUserName?: string;
  password?: string;
  storageUrl?: string;
  login?: string;
  uri?: string;
  recorderId?: string;
  hostName?: string;
  name?: string;
}

export interface AccountRecorderConfiguration extends Recorder {
  password?: string;
  login?: string;
  name?: string;
  statusDetails?: string;
  encryptedPassword?: string;
  storageUserName?: string;
  status?: RecorderStatus;
  uri?: string;
  id?: number;
  storageUrl?: string;
  maxSimultaneousRecordings?: number;
  recorderId?: string;
  accountDetails?: AccountDetails;
  hostName?: string;
}

export interface BanDetail {
  ban?: string;
  id?: number;
  description?: string;
  default: boolean;
}

export interface CustomizedPropertyWithValue extends CustomizedProperty {
  value?: string;
}

export interface AudioService {
  defaultService?: boolean;
  id?: number;
  name?: string;
}

export interface AudioConferencingServiceNumber {
  telUrl?: string;
  internalPhone?: string;
  audioConferencingService?: AudioConferencingService;
  name?: string;
  id?: number;
  phoneNumber?: string;
}

export interface SharedAudioConference {
  id?: number;
  hostPin?: string;
  encryptedGuestPin?: string;
  encryptedHostPin?: string;
  audioConferencingService?: AudioConferencingService;
  hostAccessCode?: string;
  opSid?: string;
  guestPin?: string;
  accessCode?: string;
}

export interface AudioConferencingService extends AudioService {
  dialInNumbers?: Array<AudioConferencingServiceNumber>;
  dialInExtension?: string;
  name?: string;
  defaultService?: boolean;
  sharedAudioConferences?: Array<SharedAudioConference>;
  id?: number;
  defaultDialInNumber?: string;
  accountDetails?: AccountDetails;
}

export interface Entitlement {
  expiration?: number;
  reportPeriod?: number;
  count?: number;
  id?: number;
  code?: string;
  active?: boolean;
  contractId?: string;
  jobId?: string;
  description?: string;
  accountId?: string;
  serviceId?: string;
  billable?: boolean;
}

export interface Channel {
  name?: string;
  id: number;
}

export interface AccountService {
  service?: Service;
  id?: AccountServiceId;
  accountDetails?: AccountDetails;
  totalLicenseCount?: number;
  usedLicenseCount?: number;
}

export interface AccountServiceId {
  accountId?: string;
  service?: Service;
}

export interface AccountDetails {
  accountBrandings?: Array<AccountBranding>;
  id?: string;
  symphonyDeploymentLocation?: SymphonyDeploymentLocation;
  helpDeskPhone?: string;
  userRegistration?: AccountUserRegistration;
  deviceCustomizedProperties?: Array<CustomizedProperty>;
  helpDeskEmail?: string;
  banBillingMethod?: BillingMethod;
  accountBillingCodes?: Array<BillingCode>;
  accountRecorderConfigs?: Array<AccountRecorderConfiguration>;
  tenantId?: string;
  customizedRoomTypes?: Array<string>;
  banDetails?: Array<BanDetail>;
  roomCustomizedProperties?: Array<CustomizedProperty>;
  account?: Account;
  graphEncryptedSecret?: string;
  accountCustomizedPropertiesWithValue?: Array<CustomizedPropertyWithValue>;
  createdDate?: number;
  audioConferencingServices?: Array<AudioConferencingService>;
  visibleAccountIds?: Array<string>;
  mapRoomTypeCounts?: { [key: string]: number };
  externalId?: string;
  clientId?: string;
  channel?: Channel;
  accountManager?: string;
  graphVersion?: string;
  lastUpdatedDate?: number;
  entitlements?: Array<Entitlement>;
  status?: string;
  serviceDetails?: Array<AccountService>;
  accountRecorderConfig?: AccountRecorderConfiguration;
  secret?: string;
  companyId?: string;
  roomOnlyMode?: boolean;
}

export enum Priority {
  Critical = "Critical",
  Major = "Major",
  Minor = "Minor",
  Informational = "Informational",
}

export enum Service {
  Standard = "Standard",
  Premier = "Premier",
  VMR = "VMR",
  VMRPoolStatic = "VMR_Pool_Static",
  VMRPoolDynamic = "VMR_Pool_Dynamic",
}

export const ServiceLabels: { [k in Service]: string } = {
  [Service.Standard]: "Standard",
  [Service.Premier]: "Conferencing",
  [Service.VMR]: "External Conferencing",
  [Service.VMRPoolStatic]: "VMR Static",
  [Service.VMRPoolDynamic]: "VMR Dynamic",
};

export enum Option {
  CommProxy = "CommProxy",
  ROOMMONITORING = "ROOM_MONITORING",
  EXTERNALSCHEDULING = "EXTERNAL_SCHEDULING",
  Premier = "Premier",
  VMR = "VMR",
  VMRPoolStatic = "VMR_Pool_Static",
  VMRPoolDynamic = "VMR_Pool_Dynamic",
  HostedMCU = "HostedMCU",
  HostedAudioConferencing = "HostedAudioConferencing",
  ProducerAttendance = "ProducerAttendance",
  Recording = "Recording",
  EXTERNALTICKETING = "EXTERNAL_TICKETING",
  SITECERTIFICATION = "SITE_CERTIFICATION",
  VMRConcurrentPorts = "VMR_Concurrent_Ports",
  Analytics = "analytics",
  Reports = "reports",
  BYOR = "buildYourOwnReports",
  LiteMode = "liteMonitoring",
  DEVICEMONITORING = "deviceMonitoring",
  enhancedPasswordsSecurity = "enhancedPasswordsSecurity",
}

export enum ServiceProvider {
  Pexip = "Pexip",
  Videxio = "Videxio",
  CMS = "CMS",
  ZoomUS = "ZoomUS",
  BlueJeans = "BlueJeans",
  WebEx = "WebEx",
  Other = "Other",
}

export enum BrandingDescription {
  FullSizeLogo = "Full_Size_Logo",
  ReducedSizeLogo = "Reduced_Size_Logo",
  EmailLogo = "Email_Logo",
  VmrTheme = "Vmr_Theme",
  LightFullSizeLogo = "Light_Full_Size_Logo",
  LightReducedSizeLogo = "Light_Reduced_Size_Logo",
  RegularSizeLogo = "Regular_Size_Logo",
}
export enum BrandingLogoClass {
  BrandingRegularSize = "BrandingRegularSize",
  // Most likely won't get used in SY6, but no harm having them referenced here.
  BrandingFullSize = "BrandingFullSize",
  BrandingReducedSize = "BrandingReducedSize",
  LightBrandingFullSize = "LightBrandingFullSize",
  LightBrandingReducedSize = "LightBrandingReducedSize",
}

export enum SymphonyDeploymentLocation {
  AVISPL = "AVI_SPL",
  OnPremises = "On_Premises",
}

export enum BillingMethod {
  MasterAccount = "MasterAccount",
  MeetingLevel = "MeetingLevel",
  SiteBySite = "SiteBySite",
}

export enum CloudConnector {
  AVIHosted = "AVI Hosted",
  ONPREM = "ONPREM CC",
}

export enum Consumption {
  SaaS = "SaaS",
  ManagedService = "Managed Service",
}

export enum Type {
  HOSTEDMCU = "HOSTED_MCU",
  PRODUCERATTENDANCE = "PRODUCER_ATTENDANCE",
  RECORDING = "RECORDING",
  HOSTEDAUDIOCONFERENCE = "HOSTED_AUDIO_CONFERENCE",
  PASSTHROUGH = "PASSTHROUGH",
}

export enum RecorderStatus {
  Ok = "Ok",
  BadCredentials = "Bad_Credentials",
  NotConfigured = "Not_Configured",
  NotEnabled = "Not_Enabled",
  NotReachable = "Not_Reachable",
}

export interface AccountFilters {
  active?: boolean;
  accountId?: string[];
  ["status.inCall"]?: boolean;
  ["status.reachable"]?: boolean;
  ["status.stale"]?: boolean;
  ["status.apiError"]?: boolean;
  ["status.loginError"]?: boolean;
  ["status.pauseMonitoringStatus.paused"]?: boolean;
  excludeSimpleAssets?: boolean;
  cloudConnector?: string;
  consumption?: string;
  entityType?: string;
  ["tenantAccount.id"]?: string;
  tags?: string[];
}

export type AuthenticationConfiguration = {
  authenticationMethod: AuthenticationConfigurationMethod;
  logoutSymphonyOnly: boolean;
  samlRegistrationId?: string;
  maxSsoSessionDurationMinutes?: number;
  saml?: SAMLConfiguration;
};
export enum AuthenticationConfigurationMethod {
  NONE = "NONE",
  SAML = "SAML",
  INHERITED = "INHERITED",
}
export type SAMLConfiguration = {
  entityId?: string;
  spBinding?: SAMLConfigurationBinding;
  issuerUri?: string;
  idpMetadataUri?: string;
  idpSingleSignOnUri?: string;
  idpSingleSignOnBinding?: SAMLConfigurationBinding;
  idpSingleSignOnSignRequest?: boolean;
  idpSingleLogoutUri?: string;
  idpSingleLogoutBinding?: SAMLConfigurationBinding;
  idpVerificationCertificate?: string;
  idpVerificationCertificateError?: string;
};
export enum SAMLConfigurationBinding {
  POST = "POST",
  REDIRECT = "REDIRECT",
}

export enum EntityType {
  Tenant = "Tenant",
  Account = "Account",
}
